import { ref, onMounted, onUnmounted } from "vue";
import { checkPay } from "~/api/user";
import eventBus, { EVENT_TYPES } from "~/utils/eventBus";

// 支付状态管理 composable
export function useOrderPolling() {
  // 状态
  const pendingOrders = ref(new Map());
  const pollingTimers = ref(new Map());
  const maxRetries = 15;
  const pollingInterval = 3000;
  const storageKey = "goods_payment_pending_orders";

  // 从本地存储加载待支付订单
  const loadFromStorage = () => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const orders = JSON.parse(stored);
        orders.forEach((order) => {
          pendingOrders.value.set(order.orderId, order);
        });
      }
    } catch (error) {
      console.error("加载待支付订单失败:", error);
    }
  };

  // 保存到本地存储
  const saveToStorage = () => {
    try {
      const orders = Array.from(pendingOrders.value.values());
      localStorage.setItem(storageKey, JSON.stringify(orders));
    } catch (error) {
      console.error("保存待支付订单失败:", error);
    }
  };

  // 添加待支付订单
  const addPendingOrder = (orderId, returnUrl, options = {}) => {
    const order = {
      orderId,
      returnUrl,
      createdAt: Date.now(),
      retryCount: 0,
      lastCheck: 0,
      ...options,
    };

    pendingOrders.value.set(orderId, order);
    saveToStorage();

    setTimeout(() => {
      startPolling(orderId);
    }, 1000);

    console.log(`添加待支付订单: ${orderId}`);
    return order;
  };

  // 移除待支付订单
  const removePendingOrder = (orderId) => {
    pendingOrders.value.delete(orderId);
    stopPolling(orderId);
    saveToStorage();

    // 删除URL上的order_id参数
    const url = new URL(window.location.href);
    url.searchParams.delete("order_id");
    window.history.replaceState({}, "", url.toString());

    console.log(`移除待支付订单: ${orderId}`);
  };

  // 获取所有待支付订单
  const getAllPendingOrders = () => {
    return Array.from(pendingOrders.value.values());
  };

  // 检查订单状态
  const checkOrderStatus = async (orderId) => {
    try {
      const { data } = await checkPay({ order_ids: orderId });
      if (
        !Array.isArray(data.list) ||
        !data.list.length ||
        data.list[0].status === 0
      ) {
        return null;
      } else {
        return data.list[0].status;
      }
    } catch (error) {
      console.error("查询订单状态失败:", error);
      throw error;
    }
  };

  // 开始轮询特定订单
  const startPolling = (orderId) => {
    if (pollingTimers.value.has(orderId)) return;

    const pollWithRetry = async (retry = 0) => {
      const order = pendingOrders.value.get(orderId);
      if (!order) {
        stopPolling(orderId);
        return;
      }

      if (retry >= maxRetries) {
        console.log(`订单 ${orderId} 轮询超时`);
        removePendingOrder(orderId);
        return;
      }

      try {
        const status = await checkOrderStatus(orderId);

        if (status == 1) {
          await handlePaymentSuccess(orderId, { status: 1 });
        } else {
          order.retryCount = retry + 1;
          order.lastCheck = Date.now();
          pendingOrders.value.set(orderId, order);
          saveToStorage();
          pollingTimers.value.set(
            orderId,
            setTimeout(() => pollWithRetry(retry + 1), pollingInterval)
          );
        }
      } catch (error) {
        console.error(`轮询订单 ${orderId} 失败:`, error);
        order.retryCount = retry + 1;
        pendingOrders.value.set(orderId, order);
        saveToStorage();
        pollingTimers.value.set(
          orderId,
          setTimeout(() => pollWithRetry(retry + 1), pollingInterval)
        );
      }
    };

    pollWithRetry();
  };

  // 停止轮询
  const stopPolling = (orderId) => {
    const timer = pollingTimers.value.get(orderId);
    if (timer) {
      clearTimeout(timer);
      pollingTimers.value.delete(orderId);
    }
  };

  // 处理支付成功
  const handlePaymentSuccess = async (orderId, orderData) => {
    const order = pendingOrders.value.get(orderId);
    if (!order) return;

    console.log(`订单 ${orderId} 支付成功:`, orderData);
    removePendingOrder(orderId);
    emitPaymentSuccess(orderId, orderData, order.returnUrl);
    await navigateToSuccess(orderId, orderData, order.returnUrl);
  };

  // 触发支付成功事件
  const emitPaymentSuccess = (orderId, orderData, returnUrl) => {
    eventBus.emit(EVENT_TYPES.PAYMENT_SUCCESS, {
      orderId,
      orderData,
      returnUrl,
    });
  };

  // 跳转到成功页面
  const navigateToSuccess = async (orderId, orderData, returnUrl) => {
    console.log(returnUrl, "returnUrlreturnUrl====");

    try {
      if (returnUrl) {
        await navigateTo(returnUrl, { replace: true });
      } else {
        await navigateTo(
          `/seer/goods-result?order_id=${orderId}&payment_status=success`,
          { replace: true }
        );
      }
    } catch (error) {
      console.error("支付成功跳转失败:", error);
    }
  };

  // 手动检查订单状态
  const manualCheckOrder = async (orderId) => {
    try {
      const status = await checkOrderStatus(orderId);
      if (status === 1) {
        await handlePaymentSuccess(orderId, { status: 1 });
        return { success: true, data: { status: 1 } };
      }
      return { success: false, data: { status } };
    } catch (error) {
      console.error("手动检查订单失败:", error);
      return { success: false, error };
    }
  };

  // 初始化
  const init = () => {
    loadFromStorage();

    // 恢复所有待支付订单的轮询
    pendingOrders.value.forEach((order, orderId) => {
      console.log(`恢复订单 ${orderId} 的轮询`);
      startPolling(orderId);
    });

    // 清理过期订单
    const now = Date.now();
    const oneHour = 60 * 60 * 1000 * 3;
    pendingOrders.value.forEach((order, orderId) => {
      if (now - order.createdAt > oneHour) {
        console.log(`清理过期订单: ${orderId}`);
        removePendingOrder(orderId);
      }
    });
  };

  // 组件挂载时初始化
  onMounted(() => {
    init();
  });

  // 组件卸载时清理
  onUnmounted(() => {
    pollingTimers.value.forEach((timer) => clearTimeout(timer));
    pollingTimers.value.clear();
  });

  return {
    addPendingOrder,
    removePendingOrder,
    getAllPendingOrders,
    manualCheckOrder,
    init,
  };
}
